import {Grid, InputLabel} from '@mui/material';
import api from 'api';
import {useGetApi} from 'common/hooks/useGetApi';
import {FormFooterButtons} from 'components/buttons/FormFooterButtons';
import Dialog from 'components/Dialog';
import {GridCentered} from 'components/GridCentered';
import Formik, {FormOnSubmitHelpers} from 'components/hook-form/Formik';
import {useFieldValueGetter} from 'components/hook-form/useFieldValueGetter';
import FormikBrowseFileInput from 'components/inputs/FormikBrowseFileInput';
import FormikCheckbox from 'components/inputs/FormikCheckbox';
import FormikTextField from 'components/inputs/FormikTextField';
import {Offset} from 'components/Offset';
import Panel from 'components/Panel';
import TestDirectory from 'features/layout/CommonPage/panels/TransportPanel/components/TestDirectory';
import React, {memo} from 'react';
import {useTranslation} from 'react-i18next';
import {BackupRequest, IBackupRequest} from 'types/diplomat/backup/BackupRequest';
import {IDiplomatFile} from 'types/DiplomatFile';
import {useDefaultBackupFileName} from './hooks';

const FULL_BACKUP_EXT = '.dbu';
const SELECTIVE_BACKUP_EXT = '.dmrg';

type Props = {
   onClose: (values?: IBackupRequest, helpers?: FormOnSubmitHelpers<IBackupRequest>) => void;
   isFull?: boolean;
};

export const BackupLocationDialog = memo(({onClose, isFull = true}: Props) => {
   const {t} = useTranslation();
   const {request, state: backupDir} = useGetApi<IDiplomatFile>(api.backupdir);
   React.useEffect(() => {
      request();
   }, [request]);

   const handleSubmit = React.useCallback(
      (values: IBackupRequest, helpers: FormOnSubmitHelpers<IBackupRequest>) => {
         const ext = isFull ? FULL_BACKUP_EXT : SELECTIVE_BACKUP_EXT;
         // values.backupFilename += ext;
         return onClose({...values, backupFilename: values.backupFilename + ext}, helpers);
      },
      [isFull, onClose]
   );

   const backupFilename = useDefaultBackupFileName();

   return (
      <Dialog maxWidth={'md'} open={true} close={onClose} header={t('Select Backup Directory')}>
         <Formik
            initialValues={BackupRequest.cast({backupDirPath: backupDir?.path, backupFilename})}
            initialTouched={['backupFilename']}
            enableReinitialize
            onSubmit={handleSubmit}
         >
            <BackupLocationForm isFull={isFull} onClose={onClose} />
         </Formik>
      </Dialog>
   );
});

function BackupLocationForm({onClose, isFull}: Props) {
   const {t} = useTranslation();
   const getValue = useFieldValueGetter<IBackupRequest>();
   const backupDirPath = getValue('backupDirPath');

   const ext = isFull ? FULL_BACKUP_EXT : SELECTIVE_BACKUP_EXT;

   return (
      <>
         <Panel>
            <Grid container spacing={2}>
               <GridCentered item xs={3}>
                  <InputLabel>{t('Backup Directory')}</InputLabel>
               </GridCentered>
               <Grid item xs={6}>
                  <FormikBrowseFileInput name="backupDirPath" directoriesOnly />
               </Grid>
               <GridCentered item xs={3}>
                  <TestDirectory directoryPath={backupDirPath} />
               </GridCentered>

               <GridCentered item xs={3}>
                  <InputLabel>{t('Backup File Name')}</InputLabel>
               </GridCentered>
               <Grid item xs={6}>
                  <FormikTextField autoFocus name="backupFilename" />
               </Grid>
               <GridCentered item xs={3}>
                  <InputLabel>{ext}</InputLabel>
               </GridCentered>

               <Offset xs={3} />
               <Grid item>
                  <FormikCheckbox name="removePasswords" label={t('Remove passwords')} />
               </Grid>
               <GridCentered item>
                  <div
                     title={t('Check this box when sending backup to support@coviantsoftware.com')}
                  >
                     ?
                  </div>
               </GridCentered>
            </Grid>
         </Panel>
         <FormFooterButtons cancel={onClose} />
      </>
   );
}
